/**
 * Captura de foto en web para ítems foto/galería y la imagen de la nota.
 * La app Cordova sigue usando el plugin nativo en answer.vue.
 */
const CAMERA_BUTTONS = [
    ['cancel-camera', 'onWebCameraCancel'],
    ['zoom-camera', 'onWebCameraZoom'],
    ['take-camera', 'onWebCameraTake'],
    ['flash-camera', 'onWebCameraFlash'],
    ['swap-camera', 'onWebCameraSwap']
]

export default {
    data() {
        return {
            openingLibraryPicker: false,
            webCameraOwned: false,
            webCameraSession: 0,
            webCameraType: 'answer',
            webCameraFacing: 'environment',
            webCameraFlash: false,
            webCameraStream: null,
            webCameraButtonsBound: false,
            webCameraButtonHandlers: []
        }
    },
    computed: {
        nativeFileCapture() {
            return process.env.VUE_APP_IS_APP === 'TRUE' ? 'camera' : null
        }
    },
    methods: {
        captureOnWeb(event, type) {
            if (this.openingLibraryPicker) {
                return
            }

            if (event) {
                event.preventDefault()
                event.stopPropagation()
            }

            const isComment = type === 'comment'
            if (this.isAuditItemInActionPlanChecklist && !isComment) {
                return
            }

            const isOfflineActive = this.$store.getters.getOffline
            if (!(window.fnCheckConnection() || isOfflineActive)) {
                this.$snackbar.error({
                    duration: 5000,
                    closeable: true,
                    message: this.$t('empty_pages.connection_description')
                })
                return
            }

            this.openWebPhotoChooser(type, isComment)
        },
        async openWebPhotoChooser(type, isComment) {
            const access = await this.checkAccess(true, isComment)
            if (!this.canOpenWebPhotoChooser(access)) {
                return
            }

            if (!isComment && this.reachedWebPhotoLimit()) {
                this.$popup.alert({ message: this.$t('answer.galery.message') })
                return
            }

            const captureMode = this.webCaptureMode(isComment)
            if (!captureMode || captureMode === 'all') {
                this.$popup.confirmUpload({
                    message: this.$t('cordova.plugins.camera.title'),
                    textSaveStart: this.$t('cordova.plugins.camera.text_camera'),
                    textSave: this.$t('cordova.plugins.camera.text_library'),
                    callSaveStart: () => {
                        this.openWebCamera(type)
                    },
                    callSave: () => {
                        this.openWebLibrary(type)
                    }
                })
                return
            }

            if (captureMode === 'camera') {
                this.$popup.confirm({
                    message: this.$t('cordova.plugins.camera.title'),
                    textSave: this.$t('cordova.plugins.camera.text_camera'),
                    callSave: () => {
                        this.openWebCamera(type)
                    }
                })
                return
            }

            if (captureMode === 'gallery') {
                this.$popup.confirm({
                    message: this.$t('cordova.plugins.camera.title'),
                    textSave: this.$t('cordova.plugins.camera.text_library'),
                    callSave: () => {
                        this.openWebLibrary(type)
                    }
                })
            }
        },
        canOpenWebPhotoChooser(access) {
            if (access === true) {
                return true
            }

            const auditBlocked = Number(this.cType) === 2 && !this.isCreator
            return !auditBlocked && this.item.require_pin && this.hasAccess && this.loggedUser && this.canBeEdited && !this.isDisabled
        },
        webCaptureMode(isComment) {
            if (isComment) {
                return this.$store.getters.getNoteImg
            }

            return this.item && this.item.config ? this.item.config.capture : null
        },
        reachedWebPhotoLimit() {
            if (!this.aConfig || typeof this.aConfig.limit === 'undefined') {
                return false
            }

            const photos = Array.isArray(this.value) ? this.value : []
            return photos.length >= this.aConfig.limit && Number(this.aConfig.limit) !== 1
        },
        openWebLibrary(type) {
            const input = this.$refs[type === 'comment' ? 'commentFileInput' : 'answerFileInput']
            if (!input) {
                return
            }

            this.openingLibraryPicker = true
            input.value = ''
            input.click()
            this.openingLibraryPicker = false
        },
        showCameraUnavailable() {
            this.$snackbar.error({
                duration: 5000,
                closeable: true,
                message: this.$t('cordova.plugins.camera.error_unavailable')
            })
        },
        async listVideoInputs() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                return []
            }

            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                return devices.filter((device) => device.kind === 'videoinput')
            } catch (error) {
                return []
            }
        },
        openWebCamera(type) {
            this.webCameraOwned = true
            this.webCameraType = type
            this.webCameraFacing = 'environment'
            this.webCameraFlash = false
            this.webCameraSession += 1
            const session = this.webCameraSession
            // getUserMedia tiene que arrancar en el mismo gesto que el click del popup.
            const streamPromise = this.requestCameraStream(this.webCameraFacing)
            this.attachWebCamera(session, streamPromise)
        },
        async attachWebCamera(session, streamPromise) {
            const stream = await streamPromise
            const cameras = await this.listVideoInputs()
            if (session !== this.webCameraSession) {
                this.stopStream(stream)
                return
            }

            if (!stream || cameras.length === 0) {
                this.stopStream(stream)
                this.webCameraOwned = false
                this.showCameraUnavailable()
                return
            }

            this.webCameraStream = stream
            this.$store.commit('setCameraCurrentZoom', 'x1')
            this.$store.commit('setCameraActive', true)

            const video = await this.waitForCameraVideo()
            if (session !== this.webCameraSession) {
                this.stopStream(stream)
                return
            }

            if (!video) {
                this.stopWebCamera()
                this.showCameraUnavailable()
                return
            }

            video.srcObject = stream
            try {
                await video.play()
            } catch (error) {
                // El stream sigue asociado aunque el navegador bloquee el autoplay.
            }

            if (session !== this.webCameraSession) {
                return
            }

            this.bindWebCameraButtons()
        },
        async waitForCameraVideo() {
            for (let attempt = 0; attempt < 5; attempt += 1) {
                await this.$nextTick()
                const video = document.querySelector('#app .camera video')
                if (video) {
                    return video
                }
            }

            return null
        },
        async requestCameraStream(facing) {
            const attempts = []
            if (facing) {
                attempts.push({ video: { facingMode: { ideal: facing } }, audio: false })
            }
            attempts.push({ video: true, audio: false })

            for (let index = 0; index < attempts.length; index += 1) {
                try {
                    return await navigator.mediaDevices.getUserMedia(attempts[index])
                } catch (error) {
                    // Se prueba la siguiente restricción.
                }
            }

            return null
        },
        async startWebCameraPreview(video, session) {
            this.stopWebCameraStream()
            const stream = await this.requestCameraStream(this.webCameraFacing)
            if (session !== this.webCameraSession) {
                this.stopStream(stream)
                return false
            }

            if (!stream) {
                return false
            }

            this.webCameraStream = stream
            video.srcObject = stream
            try {
                await video.play()
            } catch (error) {
                // El stream sigue asociado aunque el navegador bloquee el autoplay.
            }

            this.bindWebCameraButtons()
            return true
        },
        stopStream(stream) {
            if (!stream) {
                return
            }

            stream.getTracks().forEach((track) => {
                track.stop()
            })
        },
        stopWebCameraStream() {
            this.stopStream(this.webCameraStream)
            this.webCameraStream = null
            const video = document.querySelector('#app .camera video')
            if (video) {
                video.srcObject = null
            }
        },
        webCameraTrack() {
            if (!this.webCameraStream) {
                return null
            }

            const tracks = this.webCameraStream.getVideoTracks()
            return tracks.length ? tracks[0] : null
        },
        bindWebCameraButtons() {
            if (this.webCameraButtonsBound) {
                return
            }

            this.webCameraButtonHandlers = []
            CAMERA_BUTTONS.forEach(([id, method]) => {
                const element = document.getElementById(id)
                if (!element) {
                    return
                }

                const handler = (event) => {
                    this[method](event)
                }
                element.addEventListener('click', handler)
                this.webCameraButtonHandlers.push({ element, handler })
            })
            this.webCameraButtonsBound = this.webCameraButtonHandlers.length > 0
        },
        unbindWebCameraButtons() {
            this.webCameraButtonHandlers.forEach(({ element, handler }) => {
                element.removeEventListener('click', handler)
            })
            this.webCameraButtonHandlers = []
            this.webCameraButtonsBound = false
        },
        stopWebCamera() {
            if (!this.webCameraOwned) {
                return
            }

            this.webCameraSession += 1
            this.unbindWebCameraButtons()
            this.stopWebCameraStream()
            this.webCameraOwned = false
            this.webCameraFlash = false
            this.$store.commit('setCameraActive', false)
            this.$store.commit('setCameraCurrentZoom', 'x1')
        },
        onWebCameraCancel(event) {
            event.preventDefault()
            event.stopPropagation()
            this.stopWebCamera()
        },
        async onWebCameraSwap(event) {
            event.preventDefault()
            event.stopPropagation()

            if (this.webCameraFlash) {
                await this.applyTorch(false)
                this.webCameraFlash = false
                this.syncFlashButton()
            }

            const previousFacing = this.webCameraFacing
            this.webCameraFacing = previousFacing === 'environment' ? 'user' : 'environment'
            const session = this.webCameraSession
            const video = document.querySelector('#app .camera video')
            if (!video) {
                return
            }

            const started = await this.startWebCameraPreview(video, session)
            if (started || session !== this.webCameraSession) {
                return
            }

            this.webCameraFacing = previousFacing
            const restored = await this.startWebCameraPreview(video, session)
            if (!restored && session === this.webCameraSession) {
                this.stopWebCamera()
                this.showCameraUnavailable()
            }
        },
        async onWebCameraFlash(event) {
            event.preventDefault()
            event.stopPropagation()

            if (this.webCameraFacing !== 'environment') {
                return
            }

            const nextFlash = !this.webCameraFlash
            const applied = await this.applyTorch(nextFlash)
            if (!applied) {
                return
            }

            this.webCameraFlash = nextFlash
            this.syncFlashButton()
        },
        syncFlashButton() {
            const button = document.getElementById('flash-camera')
            if (!button) {
                return
            }

            button.classList.toggle('bounce', !this.webCameraFlash)
        },
        async applyTorch(enabled) {
            const track = this.webCameraTrack()
            if (!track || typeof track.getCapabilities !== 'function') {
                return false
            }

            const capabilities = track.getCapabilities()
            if (!capabilities.torch) {
                return false
            }

            try {
                await track.applyConstraints({ advanced: [{ torch: enabled }] })
                return true
            } catch (error) {
                return false
            }
        },
        async onWebCameraZoom(event) {
            event.preventDefault()
            event.stopPropagation()

            const track = this.webCameraTrack()
            if (!track || typeof track.getCapabilities !== 'function') {
                return
            }

            const capabilities = track.getCapabilities()
            if (!capabilities.zoom) {
                return
            }

            const minZoom = capabilities.zoom.min
            const maxZoom = capabilities.zoom.max
            const settings = track.getSettings()
            let currentZoom = settings.zoom || minZoom
            let label = 'x1'

            if (currentZoom === minZoom) {
                if (currentZoom * 2 < maxZoom) {
                    currentZoom = currentZoom * 2
                    label = 'x2'
                } else {
                    currentZoom = maxZoom
                    label = 'x5'
                }
            } else if (currentZoom === maxZoom) {
                currentZoom = minZoom
                label = 'x1'
            } else {
                currentZoom = maxZoom
                label = 'x5'
            }

            try {
                await track.applyConstraints({ advanced: [{ zoom: currentZoom }] })
                this.$store.commit('setCameraCurrentZoom', label)
            } catch (error) {
                // El botón se queda visible aunque el zoom no se pueda aplicar.
            }
        },
        async onWebCameraTake(event) {
            event.preventDefault()
            event.stopPropagation()

            const dataUrl = await this.captureWebFrame()
            if (!dataUrl) {
                this.$snackbar.error({
                    closeable: true,
                    message: this.$t('popup.media.image_empty')
                })
                return
            }

            this.applyCapturedWebPhoto(dataUrl)
            this.stopWebCamera()
        },
        captureWebFrame() {
            const video = document.querySelector('#app .camera video')
            if (!video || !video.videoWidth || !video.videoHeight) {
                return Promise.resolve(null)
            }

            const snapshot = document.createElement('canvas')
            snapshot.width = video.videoWidth
            snapshot.height = video.videoHeight
            const context = snapshot.getContext('2d')
            if (!context) {
                return Promise.resolve(null)
            }

            context.drawImage(video, 0, 0)
            const image = new window.Image()

            return new Promise((resolve) => {
                image.onload = () => {
                    resolve(this.resizeImage(image))
                }
                image.onerror = () => {
                    resolve(null)
                }
                image.src = snapshot.toDataURL('image/png')
            })
        },
        applyCapturedWebPhoto(dataUrl) {
            if (typeof this.receiveWebCameraPhoto === 'function') {
                this.receiveWebCameraPhoto(dataUrl, this.webCameraType)
                return
            }

            if (this.webCameraType === 'comment') {
                this.image = dataUrl
                return
            }

            let photos = Array.isArray(this.value) ? this.value.slice() : []
            if (this.aConfig && Number(this.aConfig.limit) === 1) {
                photos = []
            }

            photos.push(dataUrl)
            this.value = JSON.parse(JSON.stringify(photos))
        }
    },
    beforeDestroy() {
        this.stopWebCamera()
    }
}
